import { ActionReducerMapBuilder, createSlice } from '@reduxjs/toolkit';
import {
  ExcelSettlementReport,
  OrderSettlementQueryListResponse,
  SettlementItemInfoResponse,
  SettlementQueryListResponse,
} from '@models/response/settlment';
import {
  getSettlementsService,
  getSettlementsInfoById,
  getSettlementsOrderInfoById,
  getSettlementExcel,
} from '@services/settlement';
import { createAppAsyncThunk } from '../utils/reduxUtil';
import NUMBER_TABLE_PER_PAGE from '../shared/constants/numberPerPage';

interface VendorSettlementsStateInterface {
  loading: boolean;
  settlements: SettlementQueryListResponse | undefined;
  settlement: OrderSettlementQueryListResponse | undefined;
  settlementInfo: SettlementItemInfoResponse | undefined;
  pageSize: number;
  pageNumber: number;
  search: string;
  infoPageNumber: number;
  excelLink: ExcelSettlementReport | undefined;
  recordLoading: boolean;
}

const initialState: VendorSettlementsStateInterface = {
  loading: false,
  settlement: undefined,
  settlements: undefined,
  pageSize: NUMBER_TABLE_PER_PAGE,
  pageNumber: 1,
  search: '',
  infoPageNumber: 1,
  settlementInfo: undefined,
  excelLink: undefined,
  recordLoading: false,
};

const getSettlements = createAppAsyncThunk<SettlementQueryListResponse, void>(
  'settlements/mySettlements',
  async (_, { getState }) => {
    const { pageNumber, pageSize, search } = getState().vendorSettlements;
    const response = await getSettlementsService({
      PageNumber: pageNumber,
      PageSize: pageSize,
      Search: search,
    });
    return response;
  }
);

const getSettlementById = createAppAsyncThunk<OrderSettlementQueryListResponse, string>(
  'settlements/mySettlement',
  async (settlementId, { getState }) => {
    const { infoPageNumber, pageSize, search } = getState().vendorSettlements;
    const response = await getSettlementsOrderInfoById({
      SettlementId: settlementId,
      PageNumber: infoPageNumber,
      PageSize: pageSize,
      Search: search,
    });
    return response;
  }
);

const getSettlementInfo = createAppAsyncThunk<SettlementItemInfoResponse, string>(
  'settlements/mySettlementInfo',
  async (id) => {
    const response = await getSettlementsInfoById(id);
    return response;
  }
);

const excelSettlementsExport = createAppAsyncThunk<ExcelSettlementReport, string>(
  'settlements/mySettlementExcel',
  async (id) => {
    const response = await getSettlementExcel(id);
    return response;
  }
);

const settlementsSlice = createSlice({
  name: 'settlements',
  initialState,
  reducers: {
    setPageNumber: (state, action) => ({
      ...state,
      pageNumber: action.payload,
    }),
    setPageNumberInfo: (state, action) => ({
      ...state,
      infoPageNumber: action.payload,
    }),
    setSearch: (state, action) => ({
      ...state,
      search: action.payload,
    }),
  },
  extraReducers: (builder: ActionReducerMapBuilder<VendorSettlementsStateInterface>) => {
    builder.addCase(getSettlements.pending, (state) => ({
      ...state,
      loading: true,
    }));

    builder.addCase(getSettlements.fulfilled, (state, action) => ({
      ...state,
      loading: false,
      settlements: action?.payload,
    }));

    builder.addCase(getSettlements.rejected, (state) => ({
      ...state,
      loading: false,
    }));

    builder.addCase(getSettlementById.pending, (state) => ({
      ...state,
      recordLoading: true,
    }));

    builder.addCase(getSettlementById.fulfilled, (state, action) => ({
      ...state,
      recordLoading: false,
      settlement: action?.payload,
    }));

    builder.addCase(getSettlementInfo.pending, (state) => ({
      ...state,
      recordLoading: true,
    }));

    builder.addCase(getSettlementInfo.fulfilled, (state, action) => ({
      ...state,
      loading: false,
      settlementInfo: action?.payload,
    }));
    builder.addCase(getSettlementInfo.rejected, (state) => ({
      ...state,
      loading: false,
    }));

    builder.addCase(excelSettlementsExport.pending, (state) => ({
      ...state,
      loading: true,
    }));

    builder.addCase(excelSettlementsExport.fulfilled, (state, action) => ({
      ...state,
      loading: false,
      excelLink: action?.payload,
    }));

    builder.addCase(excelSettlementsExport.rejected, (state) => ({
      ...state,
      loading: false,
    }));
  },
});

export default settlementsSlice;
export { getSettlementById, getSettlements, getSettlementInfo, excelSettlementsExport };
